import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Sliders, LogOut, CheckCircle } from "lucide-react";
import { colors, type, spacing } from "@/theme";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import KdsTableCard, { NEXT } from "@/components/KdsTableCard";

export default function Kds() {
  const navigate = useNavigate();
  const { user, ready, logout } = useAuth();
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmPay, setConfirmPay] = useState(null);
  const [pulse, setPulse] = useState(0);

  useEffect(() => { document.title = "Cucina (KDS) | Tavoline"; }, []);

  const load = useCallback(async () => {
    try { const res = await api.kdsTables(); setTables(res.tables); } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!ready) return;
    if (!user) { navigate("/staff/login", { replace: true }); return; }
    load();
    const poll = setInterval(load, 5000);
    const p = setInterval(() => setPulse((v) => (v >= 1 ? 0 : v + 0.25)), 350);
    return () => { clearInterval(poll); clearInterval(p); };
  }, [user, ready, load, navigate]);

  const advance = async (orderId, stato) => {
    const next = NEXT[stato];
    if (!next) return;
    await api.setOrderStatus(orderId, next);
    load();
  };

  const pay = async (sessionId) => {
    setConfirmPay(null);
    await api.paySession(sessionId);
    load();
  };

  const activeTables = tables.length;
  const openOrders = tables.reduce((s, t) => s + t.orders.filter((o) => !["servito", "annullato"].includes(o.stato)).length, 0);

  return (
    <div style={{ minHeight: "100vh", background: colors.bg }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: `${spacing.md}px ${spacing.lg}px`, borderBottom: `1px solid ${colors.line}` }}>
        <div>
          <div style={{ ...type.sansBold, fontSize: 26, color: colors.ink }}>Cucina</div>
          <div style={{ ...type.sans, fontSize: 13, color: colors.inkSoft }}>{user?.tenant_nome}</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: spacing.md }}>
          <div style={{ textAlign: "center" }}><div style={{ ...type.sansBold, fontSize: 22, color: colors.accent }}>{activeTables}</div><div style={{ ...type.sans, fontSize: 11, color: colors.inkSoft }}>tavoli</div></div>
          <div style={{ textAlign: "center" }}><div style={{ ...type.sansBold, fontSize: 22, color: colors.accent }}>{openOrders}</div><div style={{ ...type.sans, fontSize: 11, color: colors.inkSoft }}>in corso</div></div>
          <button data-testid="kds-admin-button" onClick={() => navigate("/admin")} style={headBtn}><Sliders size={20} color={colors.ink} /></button>
          <button data-testid="logout-button" onClick={async () => { await logout(); navigate("/", { replace: true }); }} style={headBtn}><LogOut size={20} color={colors.ink} /></button>
        </div>
      </div>

      {loading ? <div style={{ padding: spacing.xxl, textAlign: "center", ...type.sans, color: colors.accent }}>Caricamento…</div>
        : tables.length === 0 ? (
          <div style={{ padding: spacing.xxl, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: spacing.md }}>
            <CheckCircle size={40} color={colors.inkSoft} />
            <div style={{ ...type.serif, fontSize: 20, color: colors.inkSoft }}>Nessun tavolo aperto</div>
          </div>
        ) : (
          <div style={{ padding: spacing.md, display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: spacing.md, alignItems: "start" }}>
            {tables.map((t) => (
              <KdsTableCard key={t.session_id} t={t} pulse={pulse} onAdvance={advance} confirmPay={confirmPay} setConfirmPay={setConfirmPay} onPay={pay} />
            ))}
          </div>
        )}
    </div>
  );
}

const headBtn = { width: 40, height: 40, borderRadius: 20, background: colors.surface, border: `1px solid ${colors.line}`, display: "flex", alignItems: "center", justifyContent: "center" };
